/**
 * Pure functions for caption overlay rendering on screencast frames.
 *
 * Kept separate from browser-recording.ts so the lifecycle/tool code stays
 * focused and the pixel-level logic is easy to test.
 */

import { decode as decodeJpegBytes, encode as encodeJpegBytes } from 'jpeg-js';

/** A caption is a short piece of agent-authored text shown for a window of time. */
export interface RecordingCaption {
  /** Milliseconds since recording start when the caption was added. */
  timestampMs: number;
  /** Plain text caption. */
  text: string;
  /** How long the caption should remain visible (ms). */
  durationMs: number;
}

/** Decoded RGBA frame buffer. */
export interface RgbaFrame {
  width: number;
  height: number;
  /** Tightly packed RGBA pixels (width * height * 4 bytes). */
  data: Uint8ClampedArray;
}

/**
 * Pick the caption that should be visible at a given timestamp.
 *
 * If multiple captions overlap (a new caption starts before the previous
 * expires), the newer one wins.
 *
 * Returns undefined when no caption is active at `t`.
 */
export function selectCaptionAt(captions: readonly RecordingCaption[], t: number): RecordingCaption | undefined {
  let active: RecordingCaption | undefined;
  for (const c of captions) {
    if (t >= c.timestampMs && t < c.timestampMs + c.durationMs) {
      if (!active || c.timestampMs > active.timestampMs) {
        active = c;
      }
    }
  }
  return active;
}

/**
 * Decode a JPEG buffer into an RGBA pixel array.
 * Throws when the JPEG cannot be parsed.
 */
export function decodeJpeg(jpegBytes: Uint8Array): RgbaFrame {
  const decoded = decodeJpegBytes(jpegBytes, { useTArray: true, formatAsRGBA: true });
  return {
    width: decoded.width,
    height: decoded.height,
    data: new Uint8ClampedArray(decoded.data.buffer, decoded.data.byteOffset, decoded.data.byteLength),
  };
}

// ---------------------------------------------------------------------------
// Bitmap font
// ---------------------------------------------------------------------------

/**
 * Minimal 5x7 bitmap font covering printable ASCII (0x20..0x7E).
 * Each glyph is 5 columns × 7 rows, encoded as 7 bytes (one per row).
 * Bit 4 (0x10) of each row byte is the leftmost pixel.
 *
 * This is intentionally tiny so we don't need a TrueType/system font.
 */
const FONT_5x7: Record<string, readonly number[]> = {
  ' ': [0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00],
  '!': [0x04, 0x04, 0x04, 0x04, 0x00, 0x00, 0x04],
  '"': [0x0a, 0x0a, 0x00, 0x00, 0x00, 0x00, 0x00],
  '#': [0x0a, 0x1f, 0x0a, 0x1f, 0x0a, 0x00, 0x00],
  $: [0x04, 0x0f, 0x14, 0x0e, 0x05, 0x1e, 0x04],
  '%': [0x19, 0x19, 0x02, 0x04, 0x08, 0x13, 0x13],
  '&': [0x08, 0x14, 0x14, 0x08, 0x15, 0x12, 0x0d],
  "'": [0x04, 0x04, 0x00, 0x00, 0x00, 0x00, 0x00],
  '(': [0x02, 0x04, 0x08, 0x08, 0x08, 0x04, 0x02],
  ')': [0x08, 0x04, 0x02, 0x02, 0x02, 0x04, 0x08],
  '*': [0x00, 0x04, 0x15, 0x0e, 0x15, 0x04, 0x00],
  '+': [0x00, 0x04, 0x04, 0x1f, 0x04, 0x04, 0x00],
  ',': [0x00, 0x00, 0x00, 0x00, 0x00, 0x04, 0x08],
  '-': [0x00, 0x00, 0x00, 0x1f, 0x00, 0x00, 0x00],
  '.': [0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x04],
  '/': [0x00, 0x01, 0x02, 0x04, 0x08, 0x10, 0x00],
  '0': [0x0e, 0x11, 0x13, 0x15, 0x19, 0x11, 0x0e],
  '1': [0x04, 0x0c, 0x04, 0x04, 0x04, 0x04, 0x0e],
  '2': [0x0e, 0x11, 0x01, 0x02, 0x04, 0x08, 0x1f],
  '3': [0x1f, 0x02, 0x04, 0x02, 0x01, 0x11, 0x0e],
  '4': [0x02, 0x06, 0x0a, 0x12, 0x1f, 0x02, 0x02],
  '5': [0x1f, 0x10, 0x1e, 0x01, 0x01, 0x11, 0x0e],
  '6': [0x06, 0x08, 0x10, 0x1e, 0x11, 0x11, 0x0e],
  '7': [0x1f, 0x01, 0x02, 0x04, 0x08, 0x08, 0x08],
  '8': [0x0e, 0x11, 0x11, 0x0e, 0x11, 0x11, 0x0e],
  '9': [0x0e, 0x11, 0x11, 0x0f, 0x01, 0x02, 0x0c],
  ':': [0x00, 0x00, 0x04, 0x00, 0x00, 0x04, 0x00],
  ';': [0x00, 0x00, 0x04, 0x00, 0x00, 0x04, 0x08],
  '<': [0x02, 0x04, 0x08, 0x10, 0x08, 0x04, 0x02],
  '=': [0x00, 0x00, 0x1f, 0x00, 0x1f, 0x00, 0x00],
  '>': [0x08, 0x04, 0x02, 0x01, 0x02, 0x04, 0x08],
  '?': [0x0e, 0x11, 0x01, 0x02, 0x04, 0x00, 0x04],
  '@': [0x0e, 0x11, 0x17, 0x15, 0x17, 0x10, 0x0e],
  A: [0x0e, 0x11, 0x11, 0x1f, 0x11, 0x11, 0x11],
  B: [0x1e, 0x11, 0x11, 0x1e, 0x11, 0x11, 0x1e],
  C: [0x0e, 0x11, 0x10, 0x10, 0x10, 0x11, 0x0e],
  D: [0x1c, 0x12, 0x11, 0x11, 0x11, 0x12, 0x1c],
  E: [0x1f, 0x10, 0x10, 0x1e, 0x10, 0x10, 0x1f],
  F: [0x1f, 0x10, 0x10, 0x1e, 0x10, 0x10, 0x10],
  G: [0x0e, 0x11, 0x10, 0x17, 0x11, 0x11, 0x0f],
  H: [0x11, 0x11, 0x11, 0x1f, 0x11, 0x11, 0x11],
  I: [0x0e, 0x04, 0x04, 0x04, 0x04, 0x04, 0x0e],
  J: [0x07, 0x02, 0x02, 0x02, 0x02, 0x12, 0x0c],
  K: [0x11, 0x12, 0x14, 0x18, 0x14, 0x12, 0x11],
  L: [0x10, 0x10, 0x10, 0x10, 0x10, 0x10, 0x1f],
  M: [0x11, 0x1b, 0x15, 0x15, 0x11, 0x11, 0x11],
  N: [0x11, 0x11, 0x19, 0x15, 0x13, 0x11, 0x11],
  O: [0x0e, 0x11, 0x11, 0x11, 0x11, 0x11, 0x0e],
  P: [0x1e, 0x11, 0x11, 0x1e, 0x10, 0x10, 0x10],
  Q: [0x0e, 0x11, 0x11, 0x11, 0x15, 0x12, 0x0d],
  R: [0x1e, 0x11, 0x11, 0x1e, 0x14, 0x12, 0x11],
  S: [0x0e, 0x11, 0x10, 0x0e, 0x01, 0x11, 0x0e],
  T: [0x1f, 0x04, 0x04, 0x04, 0x04, 0x04, 0x04],
  U: [0x11, 0x11, 0x11, 0x11, 0x11, 0x11, 0x0e],
  V: [0x11, 0x11, 0x11, 0x11, 0x11, 0x0a, 0x04],
  W: [0x11, 0x11, 0x11, 0x15, 0x15, 0x15, 0x0a],
  X: [0x11, 0x11, 0x0a, 0x04, 0x0a, 0x11, 0x11],
  Y: [0x11, 0x11, 0x11, 0x0a, 0x04, 0x04, 0x04],
  Z: [0x1f, 0x01, 0x02, 0x04, 0x08, 0x10, 0x1f],
  '[': [0x0e, 0x08, 0x08, 0x08, 0x08, 0x08, 0x0e],
  '\\': [0x00, 0x10, 0x08, 0x04, 0x02, 0x01, 0x00],
  ']': [0x0e, 0x02, 0x02, 0x02, 0x02, 0x02, 0x0e],
  '^': [0x04, 0x0a, 0x11, 0x00, 0x00, 0x00, 0x00],
  _: [0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x1f],
  '`': [0x08, 0x04, 0x00, 0x00, 0x00, 0x00, 0x00],
  a: [0x00, 0x00, 0x0e, 0x01, 0x0f, 0x11, 0x0f],
  b: [0x10, 0x10, 0x16, 0x19, 0x11, 0x11, 0x1e],
  c: [0x00, 0x00, 0x0e, 0x11, 0x10, 0x11, 0x0e],
  d: [0x01, 0x01, 0x0d, 0x13, 0x11, 0x11, 0x0f],
  e: [0x00, 0x00, 0x0e, 0x11, 0x1f, 0x10, 0x0e],
  f: [0x02, 0x05, 0x04, 0x0e, 0x04, 0x04, 0x04],
  g: [0x00, 0x00, 0x0f, 0x11, 0x0f, 0x01, 0x0e],
  h: [0x10, 0x10, 0x16, 0x19, 0x11, 0x11, 0x11],
  i: [0x04, 0x00, 0x0c, 0x04, 0x04, 0x04, 0x0e],
  j: [0x02, 0x00, 0x06, 0x02, 0x02, 0x12, 0x0c],
  k: [0x10, 0x10, 0x12, 0x14, 0x18, 0x14, 0x12],
  l: [0x0c, 0x04, 0x04, 0x04, 0x04, 0x04, 0x0e],
  m: [0x00, 0x00, 0x1a, 0x15, 0x15, 0x11, 0x11],
  n: [0x00, 0x00, 0x16, 0x19, 0x11, 0x11, 0x11],
  o: [0x00, 0x00, 0x0e, 0x11, 0x11, 0x11, 0x0e],
  p: [0x00, 0x00, 0x1e, 0x11, 0x1e, 0x10, 0x10],
  q: [0x00, 0x00, 0x0d, 0x13, 0x0f, 0x01, 0x01],
  r: [0x00, 0x00, 0x16, 0x19, 0x10, 0x10, 0x10],
  s: [0x00, 0x00, 0x0e, 0x10, 0x0e, 0x01, 0x1e],
  t: [0x04, 0x04, 0x0e, 0x04, 0x04, 0x05, 0x02],
  u: [0x00, 0x00, 0x11, 0x11, 0x11, 0x13, 0x0d],
  v: [0x00, 0x00, 0x11, 0x11, 0x11, 0x0a, 0x04],
  w: [0x00, 0x00, 0x11, 0x11, 0x15, 0x15, 0x0a],
  x: [0x00, 0x00, 0x11, 0x0a, 0x04, 0x0a, 0x11],
  y: [0x00, 0x00, 0x11, 0x11, 0x0f, 0x01, 0x0e],
  z: [0x00, 0x00, 0x1f, 0x02, 0x04, 0x08, 0x1f],
  '{': [0x02, 0x04, 0x04, 0x08, 0x04, 0x04, 0x02],
  '|': [0x04, 0x04, 0x04, 0x04, 0x04, 0x04, 0x04],
  '}': [0x08, 0x04, 0x04, 0x02, 0x04, 0x04, 0x08],
  '~': [0x00, 0x00, 0x08, 0x15, 0x02, 0x00, 0x00],
};

const FONT_WIDTH = 5;
const FONT_HEIGHT = 7;
const FONT_GAP = 1;

/**
 * Replace any unsupported character with `?` so we never crash on emoji
 * or non-ASCII input. Captions are intentionally short ASCII-ish text.
 */
function safeChar(c: string): string {
  return Object.prototype.hasOwnProperty.call(FONT_5x7, c) ? c : '?';
}

/** Pixel width of a string at the given scale. */
function measureText(text: string, scale: number): number {
  const codePoints = Array.from(text);
  if (codePoints.length === 0) return 0;
  return codePoints.length * FONT_WIDTH * scale + (codePoints.length - 1) * FONT_GAP * scale;
}

interface DrawTextOpts {
  /** RGB color for the text. */
  color: [number, number, number];
  /** Integer scaling factor (1, 2, 3...). */
  scale: number;
  /** Top-left pixel position. */
  x: number;
  y: number;
}

function drawText(frame: RgbaFrame, text: string, opts: DrawTextOpts): void {
  const { width, height, data } = frame;
  const { color, scale } = opts;
  let cursorX = opts.x;
  const baseY = opts.y;

  for (const rawChar of text) {
    const ch = safeChar(rawChar);
    const glyph = FONT_5x7[ch]!;
    for (let row = 0; row < FONT_HEIGHT; row++) {
      const bits = glyph[row]!;
      for (let col = 0; col < FONT_WIDTH; col++) {
        if ((bits >> (FONT_WIDTH - 1 - col)) & 1) {
          // Fill the scale x scale block for this lit pixel.
          for (let dy = 0; dy < scale; dy++) {
            for (let dx = 0; dx < scale; dx++) {
              const px = cursorX + col * scale + dx;
              const py = baseY + row * scale + dy;
              if (px < 0 || px >= width || py < 0 || py >= height) continue;
              const idx = (py * width + px) * 4;
              data[idx] = color[0];
              data[idx + 1] = color[1];
              data[idx + 2] = color[2];
              data[idx + 3] = 255;
            }
          }
        }
      }
    }
    cursorX += (FONT_WIDTH + FONT_GAP) * scale;
  }
}

/** Options controlling how a caption is drawn over a frame. */
export interface CaptionOverlayOptions {
  /** Integer scaling factor for the bitmap font. Default 2. */
  fontScale?: number;
  /** Vertical padding (in pixels) above and below the text inside the strip. */
  padding?: number;
  /** Bottom margin between the strip and the bottom of the frame. */
  marginBottom?: number;
  /** Background RGB color. Default black. */
  backgroundColor?: [number, number, number];
  /** Background alpha 0..255. Default ~180 (semi-transparent). */
  backgroundAlpha?: number;
  /** Text RGB color. Default white. */
  textColor?: [number, number, number];
}

const DEFAULT_OPTS: Required<CaptionOverlayOptions> = {
  fontScale: 2,
  padding: 6,
  marginBottom: 8,
  backgroundColor: [0, 0, 0],
  backgroundAlpha: 180,
  textColor: [255, 255, 255],
};

/**
 * Draw a caption at the bottom of the frame in-place.
 *
 * Renders a semi-transparent dark strip with the text centered on top.
 * Long captions are truncated with an ellipsis to fit the frame width.
 */
export function drawCaptionOnFrame(frame: RgbaFrame, caption: string, options: CaptionOverlayOptions = {}): void {
  const opts: Required<CaptionOverlayOptions> = { ...DEFAULT_OPTS, ...options };
  const { width, height, data } = frame;
  if (width <= 0 || height <= 0) return;
  if (!caption || caption.trim().length === 0) return;

  const scale = Math.max(1, Math.floor(opts.fontScale));
  const textHeight = FONT_HEIGHT * scale;
  const stripHeight = textHeight + opts.padding * 2;
  const stripTop = Math.max(0, height - stripHeight - opts.marginBottom);
  const stripBottom = Math.min(height, stripTop + stripHeight);
  if (stripTop >= stripBottom) return;

  // Truncate text if it overflows the available width.
  const maxTextWidth = Math.max(0, width - 16);
  let text = caption;
  if (measureText(text, scale) > maxTextWidth) {
    const ellipsis = '...';
    const codePoints = Array.from(text);
    while (codePoints.length > 0 && measureText(codePoints.join('') + ellipsis, scale) > maxTextWidth) {
      codePoints.pop();
    }
    text = codePoints.join('') + ellipsis;
  }

  // Composite the semi-transparent background over the existing pixels.
  const [br, bg, bb] = opts.backgroundColor;
  const alpha = Math.min(255, Math.max(0, opts.backgroundAlpha)) / 255;
  for (let y = stripTop; y < stripBottom; y++) {
    for (let x = 0; x < width; x++) {
      const idx = (y * width + x) * 4;
      const r = data[idx]!;
      const g = data[idx + 1]!;
      const b = data[idx + 2]!;
      data[idx] = Math.round(r * (1 - alpha) + br * alpha);
      data[idx + 1] = Math.round(g * (1 - alpha) + bg * alpha);
      data[idx + 2] = Math.round(b * (1 - alpha) + bb * alpha);
      data[idx + 3] = 255;
    }
  }

  // Center text horizontally inside the strip.
  const textWidth = measureText(text, scale);
  const textX = Math.max(0, Math.floor((width - textWidth) / 2));
  const textY = stripTop + Math.floor((stripHeight - textHeight) / 2);

  drawText(frame, text, { color: opts.textColor, scale, x: textX, y: textY });
}

/**
 * Encode an RGBA frame back into JPEG bytes. Used by the recording pipeline
 * when re-encoding a frame after burning a caption onto it.
 */
export function encodeJpeg(frame: RgbaFrame, quality = 80): Uint8Array {
  const encoded = encodeJpegBytes(
    {
      data: Buffer.from(frame.data.buffer, frame.data.byteOffset, frame.data.byteLength),
      width: frame.width,
      height: frame.height,
    },
    quality,
  );
  return new Uint8Array(encoded.data.buffer, encoded.data.byteOffset, encoded.data.byteLength);
}
